import { useState } from 'react';
import MfaEnroll from './MfaEnroll.js';

type Step =
  | { name: 'password' }
  | { name: 'mfa'; pendingToken: string }
  | { name: 'enroll'; pendingToken: string };

export default function Login() {
  const [step, setStep] = useState<Step>({ name: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function goToApp() {
    window.location.href = '/';
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
      if (body.mfa_required) {
        setStep({ name: 'mfa', pendingToken: body.pending_token });
      } else if (body.mfa_enroll_required) {
        setStep({ name: 'enroll', pendingToken: body.pending_token });
      } else {
        goToApp();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (busy || step.name !== 'mfa') return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_token: step.pendingToken, code }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `request failed: ${res.status}`);
      goToApp();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div className="section" style={{ width: '100%', maxWidth: 360 }}>
        <h1 style={{ fontSize: 18, margin: '0 0 16px', fontWeight: 600 }}>Conductor</h1>

        {error && <div className="errors">{error}</div>}

        {step.name === 'password' && (
          <>
            <form onSubmit={submitPassword} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                type="email"
                autoComplete="username"
                placeholder="Email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ padding: '8px 10px' }}
              />
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ padding: '8px 10px' }}
              />
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Sign in
              </button>
            </form>

            <div className="card-sub" style={{ margin: '16px 0 10px', textAlign: 'center' }}>or</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a className="btn btn-outline" href="/api/auth/oauth/microsoft/start" style={{ textAlign: 'center' }}>
                Sign in with Microsoft
              </a>
              <a className="btn btn-outline" href="/api/auth/oauth/google/start" style={{ textAlign: 'center' }}>
                Sign in with Google
              </a>
            </div>
          </>
        )}

        {step.name === 'mfa' && (
          <form onSubmit={submitMfa} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="card-sub">Enter the 6-digit code from your authenticator app.</div>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ fontSize: 18, letterSpacing: 4, textAlign: 'center', padding: '8px 10px' }}
            />
            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6}>
              Verify
            </button>
          </form>
        )}

        {step.name === 'enroll' && (
          <MfaEnroll pendingToken={step.pendingToken} onDone={goToApp} />
        )}
      </div>
    </div>
  );
}
